import { motion, AnimatePresence } from "framer-motion";
import { useRef } from "react";
import { ChevronLeft, ChevronRight, RotateCcw, ArrowRight } from "lucide-react";
import { AudioCard } from "./AudioCard";
import type { ArenaPair as ArenaPairType, VoteResult } from "./data/types";

type Props = {
  pair: ArenaPairType | null;
  busy: boolean;
  canVote: boolean;
  voted: boolean;
  autoAdvanceCountdown: number | null;
  listened: { left: boolean; right: boolean };
  activeSide: "left" | "right" | null;
  onPlay: (side: "left" | "right") => void;
  onListened: (side: "left" | "right") => void;
  onVote: (result: VoteResult) => void;
  onNext: () => void;
};

export function ArenaPair({ pair, busy, canVote, voted, autoAdvanceCountdown, listened, activeSide, onPlay, onListened, onVote, onNext }: Props) {
  const rightCardRef = useRef<{ play: () => void }>(null);
  if (!pair) {
    return (
      <div className="tts-empty-state">
        <h3 className="text-base font-semibold text-foreground mb-1">No eligible pair</h3>
        <p className="text-sm text-muted-foreground">Adjust filters or add more samples for the same prompt, language, and speed.</p>
      </div>
    );
  }

  return (
    <div className="tts-arena-section">
      <div className="tts-prompt-card">
        <span className="tts-prompt-label">Prompt</span>
        <p className="tts-prompt-text">{pair.left.text}</p>
        <button className="tts-skip-btn" type="button" aria-label="Skip pair" disabled={busy} onClick={onNext}>
          <RotateCcw size={15} />
        </button>
      </div>

      <div className="tts-pair-grid">
        <AudioCard
          side="left"
          sample={pair.left}
          isActive={activeSide === "left"}
          onPlay={() => onPlay("left")}
          onListened={() => onListened("left")}
          onEnded={() => rightCardRef.current?.play()}
        />
        <AudioCard
          ref={rightCardRef}
          side="right"
          sample={pair.right}
          isActive={activeSide === "right"}
          onPlay={() => onPlay("right")}
          onListened={() => onListened("right")}
        />
      </div>

      <div className="tts-vote-section">
        <AnimatePresence>
          {canVote && (
            <motion.div
              className="tts-vote-bar"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <button type="button" className="tts-vote-btn" onClick={() => onVote("left")}>
                <ChevronLeft size={16} />
                <span>Prefer A</span>
                <kbd className="tts-vote-key">←</kbd>
              </button>
              <button type="button" className="tts-vote-btn" onClick={() => onVote("right")}>
                <kbd className="tts-vote-key">→</kbd>
                <span>Prefer B</span>
                <ChevronRight size={16} />
              </button>
              <button type="button" className="tts-skip-vote-btn" disabled={busy || voted} onClick={() => onVote("skip")}>
                Skip
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {!canVote && (
          <div className="tts-vote-bar tts-vote-bar--disabled">
            <button type="button" className="tts-vote-btn" disabled>
              <ChevronLeft size={16} />
              <span>Prefer A</span>
              <kbd className="tts-vote-key">←</kbd>
            </button>
            <button type="button" className="tts-vote-btn" disabled>
              <kbd className="tts-vote-key">→</kbd>
              <span>Prefer B</span>
              <ChevronRight size={16} />
            </button>
            <button type="button" className="tts-skip-vote-btn" disabled={busy || voted} onClick={() => onVote("skip")}>
              Skip
            </button>
          </div>
        )}

        <div className="tts-gate-hint">
          <div className={`tts-gate-dot ${listened.left ? "ready" : ""}`} />
          <div className={`tts-gate-dot ${listened.right ? "ready" : ""}`} />
          <span className="tts-gate-text">
            {voted
              ? "Vote recorded."
              : listened.left && listened.right
              ? "Both clips heard — vote to continue"
              : listened.left
              ? "Option A ready. Play Option B."
              : listened.right
              ? "Option B ready. Play Option A."
              : "Play both options for at least 3 seconds to unlock voting."}
          </span>
        </div>

        <AnimatePresence>
          {voted && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="flex justify-center pt-1"
            >
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="tts-next-btn"
                  onClick={onNext}
                >
                  Next pair
                  <ArrowRight size={15} />
                </button>
                {autoAdvanceCountdown !== null && (
                  <span className="text-xs text-muted-foreground">auto in {autoAdvanceCountdown}s</span>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
